// CountdownClock.js — Countdown timer to T-bill auction bid close.
// Shows dd:hh:mm:ss, EAT / local time, and auto-transitions on status change.

import React, { useState, useEffect, useMemo } from 'react';
import { Clock } from 'lucide-react';

const STATUS_DISPLAY = {
  pending_auction: { label: 'BIDDING OPEN', color: 'text-amber-600', bg: 'bg-amber-50' },
  bidding_closed:  { label: 'BIDDING CLOSED', color: 'text-zinc-500', bg: 'bg-zinc-100' },
  settled:         { label: 'SETTLED', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  matured:         { label: 'MATURED', color: 'text-blue-600', bg: 'bg-blue-50' },
  completed:       { label: 'COMPLETED', color: 'text-zinc-600', bg: 'bg-zinc-100' },
  cancelled:       { label: 'CANCELLED', color: 'text-red-600', bg: 'bg-red-50' },
};

function pad(n) {
  return String(n).padStart(2, '0');
}

function formatEAT(date) {
  try {
    return date.toLocaleString('en-KE', {
      timeZone: 'Africa/Nairobi',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '--:--:--';
  }
}

function formatLocal(date) {
  try {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return '--:--:--';
  }
}

export default function CountdownClock({ bidCloseAt, settlementDate, serverNow, status }) {
  const [now, setNow] = useState(() => new Date());

  // Compute server-time offset once when serverNow changes
  const serverOffset = useMemo(() => {
    if (!serverNow) return 0;
    const serverTime = new Date(serverNow).getTime();
    const clientTime = Date.now();
    return serverTime - clientTime;
  }, [serverNow]);

  // Tick every second
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date(Date.now() + serverOffset));
    }, 1000);
    return () => clearInterval(interval);
  }, [serverOffset]);

  // Compute countdown to bid close
  const countdown = useMemo(() => {
    if (!bidCloseAt) return null;
    const target = new Date(bidCloseAt).getTime();
    const diff = target - now.getTime();

    if (diff <= 0) return { expired: true, dd: '00', hh: '00', mm: '00', ss: '00' };

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return {
      expired: false,
      dd: pad(days),
      hh: pad(hours),
      mm: pad(minutes),
      ss: pad(seconds),
    };
  }, [bidCloseAt, now]);

  const statusInfo = STATUS_DISPLAY[status] || STATUS_DISPLAY.pending_auction;
  const isBiddingOpen = status === 'pending_auction';

  return (
    <div className="bg-white border border-zinc-200 rounded-none p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" style={{ color: '#4D4D4D' }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500">
            Auction Countdown
          </span>
        </div>
        <span className={`font-mono text-[10px] uppercase tracking-[0.15em] font-semibold px-2 py-0.5 ${statusInfo.color} ${statusInfo.bg}`}>
          {statusInfo.label}
        </span>
      </div>

      {/* Countdown digits */}
      {isBiddingOpen && countdown && !countdown.expired ? (
        <div className="flex items-center justify-center gap-1 mb-4">
          {[
            { value: countdown.dd, label: 'DAYS' },
            { value: countdown.hh, label: 'HRS' },
            { value: countdown.mm, label: 'MIN' },
            { value: countdown.ss, label: 'SEC' },
          ].map((segment, i) => (
            <React.Fragment key={segment.label}>
              {i > 0 && (
                <span className="font-mono text-2xl font-bold text-zinc-300 mx-0.5">:</span>
              )}
              <div className="text-center">
                <span className="font-mono text-3xl font-bold tabular-nums text-zinc-900">
                  {segment.value}
                </span>
                <p className="font-mono text-[8px] uppercase tracking-[0.15em] text-zinc-400 mt-0.5">
                  {segment.label}
                </p>
              </div>
            </React.Fragment>
          ))}
        </div>
      ) : isBiddingOpen && countdown?.expired ? (
        <div className="text-center mb-4">
          <p className="font-mono text-lg font-semibold text-amber-600">
            Bid window closed -- awaiting results
          </p>
        </div>
      ) : (
        <div className="text-center mb-4">
          <p className="font-mono text-sm text-zinc-500">
            {statusInfo.label}
          </p>
        </div>
      )}

      {/* Time display row */}
      <div className="grid grid-cols-2 gap-4 border-t border-zinc-200 pt-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
            Nairobi (EAT)
          </p>
          <p className="font-mono text-sm tabular-nums text-zinc-900">
            {formatEAT(now)}
          </p>
        </div>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
            Local Time
          </p>
          <p className="font-mono text-sm tabular-nums text-zinc-900">
            {formatLocal(now)}
          </p>
        </div>
      </div>

      {/* Settlement date */}
      {settlementDate && (
        <div className="border-t border-zinc-200 pt-3 mt-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mb-1">
            Settlement Date
          </p>
          <p className="font-mono text-sm tabular-nums text-zinc-900">
            {settlementDate}
          </p>
        </div>
      )}
    </div>
  );
}
